// src/components/pages/admin-dash/DrawsListAll.jsx
import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Icon from "../../ui/Icon";
import Badge from "../../ui/Badge";
import ConfirmDialog from "../../ui/ConfirmDialog";
import AdminTable from "./AdminTable";

const fmtUsd = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(n)
    : String(n ?? "—");

const winningNumbers = (d) =>
  [d.num1, d.num2, d.num3, d.num4, d.num5].every((n) => n != null)
    ? [d.num1, d.num2, d.num3, d.num4, d.num5]
    : null;

export default function DrawsListAll({ onNavigate, onShowToast }) {
  const [draws, setDraws]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [lotteryFilter, setLotteryFilter] = useState("ALL");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchDraws = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/admin/draw/list-all");
      setDraws(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError("Failed to load draws.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDraws(); }, []);

  const filtered = lotteryFilter === "ALL"
    ? draws
    : draws.filter((d) => (d.typeLottery || "").toUpperCase() === lotteryFilter);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/admin/draw/${deleteTarget.id}`);
      onShowToast?.(`Draw #${deleteTarget.id} deleted.`, "success");
      setDeleteTarget(null);
      fetchDraws();
    } catch (err) {
      onShowToast?.(err?.response?.data?.message || "Failed to delete draw.", "error");
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    { key: "id",          label: "ID",          sort: true, width: "70px",
      render: (d) => <span className="tabular-nums" style={{ color: "var(--txt-2)" }}>#{d.id}</span> },
    { key: "lottery",     label: "Lottery",     sort: true,
      value: (d) => d.typeLottery || "",
      render: (d) => {
        const t = (d.typeLottery || "").toUpperCase();
        if (t === "MEGA_MILLIONS") return <Badge variant="gold">Mega Millions</Badge>;
        if (t === "POWER_BALL")    return <Badge variant="blue">Powerball</Badge>;
        return <Badge variant="neutral">{t || "—"}</Badge>;
      },
    },
    { key: "date",        label: "Date",        sort: true,
      render: (d) => <span className="text-[12px]" style={{ color: "var(--txt-2)" }}>{d.date?.split?.("T")[0] || d.date || "—"}</span> },
    { key: "value",       label: "Jackpot",     sort: true, align: "right", value: (d) => Number(d.value) || 0,
      render: (d) => <span className="tabular-nums font-medium">{fmtUsd(Number(d.value))}</span> },
    { key: "numbers",     label: "Winning numbers",
      render: (d) => {
        const nums = winningNumbers(d);
        if (!nums) return <span style={{ color: "var(--txt-3)" }}>—</span>;
        return (
          <span className="tabular-nums text-[12px]" style={{ color: "var(--txt-2)" }}>
            {nums.join(", ")}{d.extraBall != null ? ` + ${d.extraBall}` : ""}
          </span>
        );
      },
    },
    { key: "actions", label: "", width: "50px", align: "right",
      render: (d) => (
        <button
          type="button"
          title="Delete draw"
          onClick={(e) => { e.stopPropagation(); setDeleteTarget(d); }}
          className="inline-flex items-center justify-center rounded-md transition-colors"
          style={{ width: 26, height: 26, color: "var(--txt-3)" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--red)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--txt-3)")}
        >
          <Icon name="trash" size={14} />
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
            Draws
          </h1>
          <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
            {filtered.length} of {draws.length} shown — click a row to edit
          </p>
        </div>
        <div className="flex items-end gap-2">
          <Button icon="refresh" size="sm" variant="ghost" onClick={fetchDraws} loading={loading}>Refresh
          </Button>
          {onNavigate && (
            <Button icon="plus" size="sm" onClick={() => onNavigate("createDraw")}>New draw
            </Button>
          )}
        </div>
      </header>

      <Card variant="soft" className="!p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="lg-label" style={{ marginRight: 4 }}>Lottery</span>
          <FilterChip label="All"             active={lotteryFilter === "ALL"}            onClick={() => setLotteryFilter("ALL")} />
          <FilterChip label="Mega Millions"   active={lotteryFilter === "MEGA_MILLIONS"}  onClick={() => setLotteryFilter("MEGA_MILLIONS")} />
          <FilterChip label="Powerball"       active={lotteryFilter === "POWER_BALL"}     onClick={() => setLotteryFilter("POWER_BALL")} />
        </div>
      </Card>

      {error && (
        <div
          className="rounded-xl px-4 py-3 text-[13px]"
          style={{
            background: "var(--red-dim)",
            border: "1px solid var(--red-border)",
            color: "var(--red)",
          }}
        >
          {error}
        </div>
      )}

      <AdminTable
        columns={columns}
        rows={filtered}
        loading={loading}
        emptyIcon="ticket"
        emptyMessage="No draws yet."
        defaultSort={{ key: "date", dir: "desc" }}
        onRowClick={onNavigate ? (d) => onNavigate("editDraw", { drawId: d.id }) : undefined}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title={deleteTarget ? `Delete draw #${deleteTarget.id}?` : ""}
        message="This permanently removes the draw record. This action can't be undone."
        confirmLabel="Delete"
        tone="danger"
        loading={deleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        icon="trash"
      />
    </div>
  );
}

function FilterChip({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3.5 py-1.5 rounded-full text-[12px] font-medium transition-colors"
      style={{
        background: active ? "var(--gold)" : "var(--surf)",
        color:      active ? "#0E2A18"     : "var(--txt-2)",
        border: `1px solid ${active ? "var(--gold)" : "var(--border-2)"}`,
      }}
    >
      {label}
    </button>
  );
}
